import React, { useMemo } from 'react';
import MiniMonth from './CccMiniMonth';
import {
  CCC_CLIENT_TYPE_COLORS,
  CCC_CLIENT_TYPE_LABELS,
  type CccClientType,
} from '../../config/cccClientRoutes';
import type { CccTeamUserOption } from '../CccTeamUserDrawer';

const CLIENT_FILTER_TYPES: CccClientType[] = [
  'recuperacao',
  'prospeccao',
  'retencao',
  'resolucao',
  'none',
];

export type ClientFilterState = Record<CccClientType, boolean>;

export const ALL_CLIENTS_ON: ClientFilterState = {
  recuperacao: true,
  prospeccao: true,
  retencao: true,
  resolucao: true,
  none: true,
};

type Props = {
  monthDate: Date;
  selectedDate: Date;
  today: Date;
  eventDays: Set<string>;
  onChangeMonth: (date: Date) => void;
  onSelectDate: (date: Date) => void;
  onCreate: () => void;
  clientFilter: ClientFilterState;
  onClientFilterChange: (next: ClientFilterState) => void;
  canPickUser: boolean;
  teamUsers: CccTeamUserOption[];
  calendarUserId: number | 'all' | null;
  onPickUser: (userId: number | 'all') => void;
  userSearch: string;
  onUserSearchChange: (value: string) => void;
};

const CccCalendarSidebar: React.FC<Props> = ({
  monthDate,
  selectedDate,
  today,
  eventDays,
  onChangeMonth,
  onSelectDate,
  onCreate,
  clientFilter,
  onClientFilterChange,
  canPickUser,
  teamUsers,
  calendarUserId,
  onPickUser,
  userSearch,
  onUserSearchChange,
}) => {
  const allClientsOn = CLIENT_FILTER_TYPES.every((t) => clientFilter[t]);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return teamUsers;
    return teamUsers.filter((u) => {
      const name = (u.name || u.username || '').toLowerCase();
      const email = (u.email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [teamUsers, userSearch]);

  return (
    <aside className="grh-cal-sidebar">
      <button type="button" className="grh-cal-create-btn" onClick={onCreate}>
        <span className="grh-cal-create-plus" aria-hidden="true">+</span>
        Novo evento
      </button>

      <MiniMonth
        monthDate={monthDate}
        selectedDate={selectedDate}
        today={today}
        eventDays={eventDays}
        onChangeMonth={onChangeMonth}
        onSelectDate={onSelectDate}
      />

      <section className="grh-cal-side-section">
        <h4 className="grh-cal-side-title">Tipos</h4>
        <label className="grh-cal-area-check area-todos">
          <input
            type="checkbox"
            checked={allClientsOn}
            onChange={(e) => {
              const on = e.target.checked;
              onClientFilterChange({
                recuperacao: on,
                prospeccao: on,
                retencao: on,
                resolucao: on,
                none: on,
              });
            }}
          />
          <span className="grh-cal-area-dot is-todos" aria-hidden="true" />
          <span>Todos</span>
        </label>
        {CLIENT_FILTER_TYPES.map((type) => (
          <label key={type} className={`grh-cal-area-check area-${type}`}>
            <input
              type="checkbox"
              checked={clientFilter[type]}
              onChange={(e) =>
                onClientFilterChange({ ...clientFilter, [type]: e.target.checked })
              }
            />
            <span
              className="grh-cal-area-dot"
              style={{ background: CCC_CLIENT_TYPE_COLORS[type] }}
              aria-hidden="true"
            />
            <span>{CCC_CLIENT_TYPE_LABELS[type]}</span>
          </label>
        ))}
      </section>

      {canPickUser && (
        <section className="grh-cal-side-section ccc-cal-user-picker">
          <h4 className="grh-cal-side-title">Calendário</h4>
          <input
            type="search"
            className="ccc-cal-user-search"
            placeholder="Procurar utilizador…"
            value={userSearch}
            onChange={(e) => onUserSearchChange(e.target.value)}
            aria-label="Procurar utilizador CCC"
          />
          <ul className="ccc-cal-user-list">
            <li>
              <button
                type="button"
                className={`ccc-cal-user-item${
                  calendarUserId === 'all' || calendarUserId === null ? ' is-active' : ''
                }`}
                onClick={() => onPickUser('all')}
              >
                Toda a equipa
              </button>
            </li>
            {filteredUsers.map((u) => (
              <li key={u.id}>
                <button
                  type="button"
                  className={`ccc-cal-user-item${
                    calendarUserId === u.id ? ' is-active' : ''
                  }`}
                  onClick={() => onPickUser(u.id)}
                  title={u.email || undefined}
                >
                  {u.name}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </aside>
  );
};

export default CccCalendarSidebar;
